import { useState } from 'react'
import { formatDueDate, formatEuro, type IncomingItem } from '@core/incoming'
import type { Space } from '@core/session'
import { Button } from '@design/components'
import { DocumentThumb } from '@features/documents/DocumentThumb'
import { DocumentViewer } from '@features/documents/DocumentViewer'
import { VoicePlayer } from '@features/documents/VoicePlayer'
import { VerifyScreen } from '@features/verification/VerifyScreen'
import { quickConfirm } from '@core/verification'
import { Overlay } from '@design/Overlay'

/**
 * Everything that came in, one at a time.
 *
 * spec.md §1877 asks for a swipeable card stack with *Klopt* and *Fix*, and
 * the reason it beats a list is rhythm: a list makes you choose what to look
 * at next, which is a decision you have to make twenty times before you have
 * dealt with anything. A stack asks one question at a time and the pile
 * visibly shrinks.
 *
 * The swipe is a shortcut, never the only way. Both buttons sit under your
 * thumb, because a gesture nobody discovers is not a feature — and because
 * "swipe right to accept a payment" should never be something you can do by
 * accident while scrolling.
 */
export function TriageScreen({
  items,
  userId,
  spaces,
  onClose,
  onChanged,
}: {
  items: IncomingItem[]
  userId: string
  spaces: Space[]
  onClose: () => void
  onChanged: () => void
}) {
  const [index, setIndex] = useState(0)
  const [verifying, setVerifying] = useState<IncomingItem | null>(null)
  const [viewing, setViewing] = useState<string | null>(null)
  const [drag, setDrag] = useState(0)
  const [start, setStart] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)

  const item = items[index]
  const remaining = items.length - index

  if (!item) {
    return (
      <Overlay>
        <div className="flex-1 grid place-items-center px-8 text-center">
          <div>
            <p className="text-lg font-medium mb-1">Alles bekeken</p>
            <p className="text-muted text-sm">Er wacht niets meer op jou.</p>
          </div>
        </div>
        <div className="shrink-0 px-4 pb-4 pad-safe-bottom">
          <Button size="large" onClick={onClose}>
            Klaar
          </Button>
        </div>
      </Overlay>
    )
  }

  function next() {
    setDrag(0)
    setStart(null)
    setRefused(null)
    setIndex((i) => i + 1)
  }

  /**
   * "Klopt" has to mean it.
   *
   * This used to call `next()` — the card slid away and nothing was written,
   * so every invoice came back the next morning and none of them could enter a
   * payment run. Now it records the verification, and when it is not allowed
   * to (red lane, changed IBAN, a field that fails its own check) it says why
   * and opens the full screen instead of pretending.
   */
  async function accept(documentId: string) {
    setBusy(true)
    setRefused(null)
    try {
      const result = await quickConfirm(documentId, userId)
      if (result.ok) {
        onChanged()
        next()
      } else {
        setRefused(result.reason)
      }
    } catch (err) {
      setRefused(err instanceof Error ? err.message : 'Dat lukte niet.')
    } finally {
      setBusy(false)
    }
  }

  // A deliberately generous threshold. Half a centimetre of thumb wobble
  // while reading must never count as a decision about money.
  const THRESHOLD = 110

  return (
    <Overlay>
      <header className="shrink-0 pad-safe-top px-3 pb-2 flex items-center justify-between border-b border-line">
        <span className="font-medium px-2">Nieuw</span>
        <span className="text-sm text-muted tabular-nums">
          nog {remaining}
        </span>
        <button onClick={onClose} className="text-muted px-2 py-2">
          Klaar
        </button>
      </header>

      <div className="flex-1 min-h-0 px-4 py-4 flex flex-col">
        <div
          className="flex-1 min-h-0 rounded-2xl bg-surface border border-line p-4 flex flex-col transition-transform"
          style={{ transform: `translateX(${drag}px) rotate(${drag / 40}deg)` }}
          onTouchStart={(e) => setStart(e.touches[0]!.clientX)}
          onTouchMove={(e) => start !== null && setDrag(e.touches[0]!.clientX - start)}
          onTouchEnd={() => {
            // Swiping right used to skip; it now means the same as "Klopt",
            // because two gestures that look identical and record different
            // things is how you end up not trusting either.
            if (drag > THRESHOLD) void accept(item.documentId)
            else if (drag < -THRESHOLD) setVerifying(item)
            else setDrag(0)
          }}
        >
          <div className="flex items-baseline justify-between gap-2">
            <p className="font-medium truncate">{item.supplier ?? 'Onbekend'}</p>
            <span className="tabular-nums shrink-0">{formatEuro(item.amountCents)}</span>
          </div>
          <p className="text-sm text-muted">
            van {item.sentBy}
            {item.dueDate && ` · ${formatDueDate(item.dueDate)}`}
          </p>
          {item.note && <p className="text-sm text-warn mt-1">{item.note}</p>}

          <VoicePlayer documentId={item.documentId} />

          <button
            onClick={() => setViewing(item.documentId)}
            className="flex-1 min-h-0 mt-3 rounded-xl overflow-hidden bg-canvas grid place-items-center"
            aria-label="Origineel bekijken"
          >
            <DocumentThumb documentId={item.documentId} className="w-full h-full !rounded-none" />
          </button>
          <p className="text-xs text-muted text-center mt-2">Tik op de foto om ze groot te zien</p>
        </div>
      </div>

      <div className="shrink-0 px-4 pb-4 pt-2 pad-safe-bottom">
        {refused && (
          <button
            onClick={() => setVerifying(item)}
            className="w-full mb-2 p-3 rounded-xl bg-warn/10 border border-warn/40 text-left"
          >
            <span className="block text-sm font-medium">{refused}</span>
            <span className="block text-sm text-muted">Tik om na te kijken.</span>
          </button>
        )}
        <div className="flex gap-2">
          <Button tone="secondary" onClick={() => setVerifying(item)}>
            Nakijken
          </Button>
          <Button disabled={busy} onClick={() => void accept(item.documentId)}>
            {busy ? 'Bezig…' : 'Klopt'}
          </Button>
        </div>
      </div>

      {viewing && (
        <DocumentViewer documentId={viewing} onClose={() => setViewing(null)} />
      )}

      {verifying && (
        <VerifyScreen
          documentId={verifying.documentId}
          userId={userId}
          spaces={spaces}
          onClose={() => setVerifying(null)}
          onDone={() => {
            setVerifying(null)
            onChanged()
            next()
          }}
        />
      )}
    </Overlay>
  )
}
